import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { FaShippingFast } from 'react-icons/fa';
import { FaUserAlt, FaMapMarkerAlt, FaPhoneAlt, FaCreditCard } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { SERVER_URL } from '../../constants';
import { toast } from 'react-toastify';

const OrderSuccess = () => {
  const location = useLocation();
  const {orderId} = useParams();
  const [loading, setLoading] = useState(true);
  const {getToken} = useAuth();
  const [fadeIn, setFadeIn] = useState(false);
  const [order, setOrder] = useState(location.state ? location.state.order : null);

  console.log(order);

  useEffect(() => {
    setFadeIn(true);
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!order) {
        try {
          setLoading(true);
          const token = await getToken();
          const response = await fetch(`${SERVER_URL}/api/v1/order/${orderId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const result = await response.json();
          if (result.success) {
            setOrder(result.data);
          } else {
            toast.error(result.message || "Failed to fetch order details");
          }
        } catch (error) {
          console.error("Error fetching order:", error);
          toast.error("Error fetching order details. Please try again later.");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [order, orderId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ClipLoader size={150} color={"#123abc"} loading={loading} />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen pt-40 mt-5 bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 transition-opacity duration-1000 ease-in-out ${
        fadeIn ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-lg rounded-xl overflow-hidden">
          {/* Header */}
          <div className="bg-green-500 p-6 text-center">
            <div className="rounded-full bg-white w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <FaShippingFast className="text-green-500 text-3xl" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Order Successful!</h1>
            <p className="text-green-100">Thank you for your purchase</p>
          </div>

          {/* Order Details */}
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-center border-b pb-4">
              <div>
                <p className="text-gray-600">Order ID</p>
                <p className="font-semibold">{order?._id}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-600">Total Amount</p>
                <p className="font-semibold text-xl">${order?.totalAmount}</p>
              </div>
            </div>

            {/* Cart Items */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Order Items</h2>
              {order && order?.cartId.items.map((item) => (
                <div
                  key={item._id}
                  className="flex items-center space-x-4 p-4 border rounded-lg hover:shadow-md transition-all duration-300 hover:scale-[1.02]"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-md"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1610024062303-e355e94c7a8c";
                    }}
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold">{item.name}</h3>
                    <div className='flex gap-2'>
                    {item.size && <p className="text-sm text-gray-600">Size: {item.size}</p>}
                    {item.color && <p className="text-sm text-gray-600">Color: {item.color}</p>}
                    </div>
                    <p className="text-sm text-gray-600">ProductCode: {item.productCode}</p>
                    <p className="text-gray-600">
                      ${item.price} x {item.quantity}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Shipping Details */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Shipping Details</h2>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center space-x-2">
                  <FaUserAlt className="text-gray-400" />
                  <p>{order?.addressDetails?.name}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <FaMapMarkerAlt className="text-gray-400" />
                  <p>
                    {order?.addressDetails?.address}, {order?.addressDetails?.city},
                    {order?.addressDetails?.state} - {order?.addressDetails?.pincode}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <FaPhoneAlt className="text-gray-400" />
                  <p>{order?.addressDetails?.phone}</p>
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Payment Information</h2>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center space-x-2">
                  <FaCreditCard className="text-gray-400" />
                  <p>{order?.paymentMethod}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className={`px-3 py-1 rounded-full text-sm ${
                      order?.paymentStatus === "Paid"
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {order?.paymentStatus}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;